import {
  Component,
  ElementRef,
  HostListener,
  Input,
  ViewChild,
  forwardRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import {
  NgpSlider,
  NgpSliderRange,
  NgpSliderThumb,
  NgpSliderTrack,
} from 'ng-primitives/slider';
import { NgpFocus } from 'ng-primitives/interactions';

import { SuiFormControlBase } from '@sensika/sui/shared';
import {
  SuiFormFieldControl,
  SUI_FORM_FIELD_CONTROL,
} from '@sensika/sui/form-field';

@Component({
  selector: 'sui-slider',
  standalone: true,
  imports: [
    NgpSlider,
    NgpSliderTrack,
    NgpSliderRange,
    NgpSliderThumb,
    NgpFocus,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SuiSliderComponent),
      multi: true,
    },
    {
      provide: SUI_FORM_FIELD_CONTROL,
      useExisting: forwardRef(() => SuiSliderComponent),
    },
  ],
  templateUrl: './sui-slider.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './sui-slider.component.scss',
})
export class SuiSliderComponent
  extends SuiFormControlBase<number>
  implements SuiFormFieldControl
{
  private static _idCounter = 0;
  private readonly _uid = `sui-slider-${++SuiSliderComponent._idCounter}`;
  private _focused = false;

  readonly controlType = 'slider';

  @ViewChild('track') private trackRef?: ElementRef<HTMLElement>;
  @ViewChild('thumb') private thumbRef?: ElementRef<HTMLElement>;

  @Input() id = '';
  @Input() min = 0;
  @Input() max = 100;
  @Input() step = 1;

  @Input()
  set disabled(val: boolean) {
    this.setDisabledState(val);
  }
  get disabled(): boolean {
    return this.isDisabled();
  }

  // The primitive's value input is one-way; default to min until a value is set.
  get sliderValue(): number {
    return this.value() ?? this.min;
  }

  get effectiveId(): string {
    return this.id || this._uid;
  }

  get focused(): boolean {
    return this._focused;
  }
  get empty(): boolean {
    return this.value() == null;
  }
  get errorState(): boolean {
    return !!(this.ngControl?.invalid && this.ngControl?.touched);
  }

  onValueChange(value: number): void {
    // Drag and keyboard both emit through here. The primitive does NOT snap
    // pointer interactions to `step` (only keyboard increments by step, and even
    // then off a possibly-unsnapped base) — drag is fully continuous. Snap here
    // so every path lands on the step grid; because the thumb/range position is
    // derived from the value, snapping the value snaps the thumb visually too.
    this.setValue(this.snapToStep(value));
  }

  /**
   * Quantize a raw value to the `[min, max]` step grid (grid origin = `min`).
   * Float-precision–safe: a decimal `step` (e.g. 0.1) would otherwise yield
   * 0.30000000000000004-style drift, so we round to the step's decimal count.
   */
  private snapToStep(raw: number): number {
    const step = this.step > 0 ? this.step : 1;
    const steps = Math.round((raw - this.min) / step);
    let snapped = this.min + steps * step;
    const decimals = (String(step).split('.')[1] ?? '').length;
    if (decimals > 0) snapped = Number(snapped.toFixed(decimals));
    return Math.min(this.max, Math.max(this.min, snapped));
  }

  /**
   * Authoritative track-click handler — works around an ng-primitives quirk.
   *
   * Because we bind `[ngpSliderValue]` (required for the form/CVA), the value is
   * "controlled": the primitive's internal `state.value.set()` becomes a no-op,
   * and the value only changes via the event → form → binding round-trip. Drag
   * and keyboard emit their freshly-computed value, so they survive that. But
   * `NgpSliderTrack`'s click emits `state.value()` *after* the no-op set, i.e. the
   * stale value — so a track click reads as "no change". (The range slider's
   * track emits its local computed value, which is why it isn't affected.)
   *
   * We listen on the wrapper, so this runs in the bubble phase *after* the
   * primitive's stale emit (which harmlessly re-sets the current value); we then
   * compute the value from the pointer position and set it ourselves, snapped to
   * the step grid (see `snapToStep`).
   *
   * We also focus the thumb on any pointer-down: the primitive's thumb handler
   * `preventDefault()`s pointerdown, which blocks the browser's click-to-focus —
   * so without this, clicking the slider never focuses it and the keyboard
   * handler (Arrow/Home/End) is unreachable except via Tab. Focusing here also
   * surfaces the active ring after a click.
   */
  onTrackPointerDown(event: PointerEvent): void {
    if (this.isDisabled()) return;
    this.thumbRef?.nativeElement.focus({ preventScroll: true });
    // Let the thumb own its drag; only bare-track clicks compute a new value.
    if ((event.target as HTMLElement).closest('.thumb')) return;
    const rect = this.trackRef?.nativeElement.getBoundingClientRect();
    if (!rect?.width) return;
    const pct = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / rect.width),
    );
    this.setValue(this.snapToStep(this.min + (this.max - this.min) * pct));
  }

  @HostListener('focusin')
  onFocusIn(): void {
    this._focused = true;
  }

  @HostListener('focusout')
  onFocusOut(): void {
    this._focused = false;
    this.markTouched();
  }
}
